/**
 * @fileoverview Объёмный рендеринг: NeRF-инференс и прямой ray marching
 * @module MikhmelViewer/Cinematic10/NeRFRenderer
 *
 * Здесь два разных инструмента под похожие задачи, и выбор между ними
 * стоит делать осознанно.
 *
 * NeRFRenderer — инференс обученной модели (multi-resolution hash grid +
 * tiny MLP, архитектура Instant-NGP) в compute-шейдере. Уместен, когда на
 * входе набор 2D-снимков с известными позами камеры и нужно синтезировать
 * ракурсы, которых не снимали.
 *
 * ОБУЧЕНИЕ В БРАУЗЕРЕ НЕ РЕАЛИЗОВАНО, и это осознанное решение, а не
 * недоделка. Обучение NeRF — это обратное распространение через хеш-решётку
 * на десятки тысяч итераций; даже Instant-NGP на RTX 4090 тратит на это
 * секунды-минуты, а WebGPU не даёт ни атомарных операций над f32, ни
 * доступа к тензорным ядрам. Правильный путь — обучать оффлайн (PyTorch,
 * tiny-cuda-nn) и грузить сюда веса через loadWeights().
 *
 * VolumeRenderer — прямой ray marching по 3D-текстуре с передаточной
 * функцией. Для КТ/МРТ это почти всегда правильный выбор: воксельный объём
 * уже есть на руках, восстанавливать его нейросетью из проекций незачем.
 * Плюс результат детерминирован и трассируем — для медицинского софта это
 * не мелочь, а требование.
 */

import { FORMATS } from './webgpuBackend.js';

const Ok = (value) => ({ success: true, value });
const Err = (error) => ({ success: false, error });

let _log = { debug: () => {}, info: () => {}, warn: () => {}, error: () => {} };
export function setVolumeLogger(logger) { if (logger) _log = logger; }

// ═══════════════════════════════════════════════════════════════════
// МАТРИЦЫ 4×4 (column-major, как в WGSL)
// ═══════════════════════════════════════════════════════════════════

export const mat4 = {
  identity() {
    return new Float32Array([1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1]);
  },

  multiply(a, b) {
    const out = new Float32Array(16);
    for (let c = 0; c < 4; c++) {
      for (let r = 0; r < 4; r++) {
        let sum = 0;
        for (let k = 0; k < 4; k++) sum += a[k * 4 + r] * b[c * 4 + k];
        out[c * 4 + r] = sum;
      }
    }
    return out;
  },

  invert(m) {
    const out = new Float32Array(16);
    const a00=m[0],a01=m[1],a02=m[2],a03=m[3];
    const a10=m[4],a11=m[5],a12=m[6],a13=m[7];
    const a20=m[8],a21=m[9],a22=m[10],a23=m[11];
    const a30=m[12],a31=m[13],a32=m[14],a33=m[15];

    const b00=a00*a11-a01*a10, b01=a00*a12-a02*a10, b02=a00*a13-a03*a10;
    const b03=a01*a12-a02*a11, b04=a01*a13-a03*a11, b05=a02*a13-a03*a12;
    const b06=a20*a31-a21*a30, b07=a20*a32-a22*a30, b08=a20*a33-a23*a30;
    const b09=a21*a32-a22*a31, b10=a21*a33-a23*a31, b11=a22*a33-a23*a32;

    let det = b00*b11 - b01*b10 + b02*b09 + b03*b08 - b04*b07 + b05*b06;
    if (Math.abs(det) < 1e-12) return mat4.identity();
    det = 1.0 / det;

    out[0]=(a11*b11-a12*b10+a13*b09)*det;
    out[1]=(a02*b10-a01*b11-a03*b09)*det;
    out[2]=(a31*b05-a32*b04+a33*b03)*det;
    out[3]=(a22*b04-a21*b05-a23*b03)*det;
    out[4]=(a12*b08-a10*b11-a13*b07)*det;
    out[5]=(a00*b11-a02*b08+a03*b07)*det;
    out[6]=(a32*b02-a30*b05-a33*b01)*det;
    out[7]=(a20*b05-a22*b02+a23*b01)*det;
    out[8]=(a10*b10-a11*b08+a13*b06)*det;
    out[9]=(a01*b08-a00*b10-a03*b06)*det;
    out[10]=(a30*b04-a31*b02+a33*b00)*det;
    out[11]=(a21*b02-a20*b04-a23*b00)*det;
    out[12]=(a11*b07-a10*b09-a12*b06)*det;
    out[13]=(a00*b09-a01*b07+a02*b06)*det;
    out[14]=(a31*b01-a30*b03-a32*b00)*det;
    out[15]=(a20*b03-a21*b01+a22*b00)*det;
    return out;
  },

  perspective(fovYRad, aspect, near, far) {
    const f = 1.0 / Math.tan(fovYRad / 2);
    const out = new Float32Array(16);
    out[0] = f / aspect;
    out[5] = f;
    out[10] = (far + near) / (near - far);
    out[11] = -1;
    out[14] = (2 * far * near) / (near - far);
    return out;
  },

  lookAt(eye, center, up) {
    const zx = eye[0]-center[0], zy = eye[1]-center[1], zz = eye[2]-center[2];
    let zl = Math.hypot(zx, zy, zz) || 1;
    const z = [zx/zl, zy/zl, zz/zl];

    const xx = up[1]*z[2]-up[2]*z[1];
    const xy = up[2]*z[0]-up[0]*z[2];
    const xz = up[0]*z[1]-up[1]*z[0];
    let xl = Math.hypot(xx, xy, xz) || 1;
    const x = [xx/xl, xy/xl, xz/xl];

    const y = [
      z[1]*x[2]-z[2]*x[1],
      z[2]*x[0]-z[0]*x[2],
      z[0]*x[1]-z[1]*x[0],
    ];

    return new Float32Array([
      x[0], y[0], z[0], 0,
      x[1], y[1], z[1], 0,
      x[2], y[2], z[2], 0,
      -(x[0]*eye[0]+x[1]*eye[1]+x[2]*eye[2]),
      -(y[0]*eye[0]+y[1]*eye[1]+y[2]*eye[2]),
      -(z[0]*eye[0]+z[1]*eye[1]+z[2]*eye[2]),
      1,
    ]);
  },
};

// ═══════════════════════════════════════════════════════════════════
// NERF RENDERER
// ═══════════════════════════════════════════════════════════════════

export const NERF_DEFAULTS = Object.freeze({
  levels: 16,
  featuresPerLevel: 2,
  hashTableSize: 1 << 19,   // 524288 записей на уровень
  baseResolution: 16,
  perLevelScale: 1.38,
  hiddenDim: 64,
  steps: 128,
  densityScale: 1.0,
  stepJitter: 1.0,
});

export class NeRFRenderer {
  constructor(backend, options = {}) {
    this._backend = backend;
    this._config = { ...NERF_DEFAULTS, ...options };
    this._weightsLoaded = false;
    this._meta = null;
    this._bbox = { min: [-1, -1, -1], max: [1, 1, 1] };
    this._disposed = false;
    this._lastRenderMs = 0;
  }

  isReady() { return this._weightsLoaded && !this._disposed && this._backend?.isReady(); }
  getConfig() { return { ...this._config }; }
  getMeta() { return this._meta ? { ...this._meta } : null; }
  getLastRenderTime() { return this._lastRenderMs; }

  /**
   * Ожидаемое количество весов MLP при текущей конфигурации.
   * Полезно, чтобы поймать несовпадение формата ещё при загрузке,
   * а не в виде мусора на экране.
   */
  getExpectedWeightCount() {
    const { levels, featuresPerLevel, hiddenDim } = this._config;
    const encDim = levels * featuresPerLevel;
    const in2 = hiddenDim + 3;
    return (
      hiddenDim * encDim + hiddenDim +   // слой 1 + смещения
      hiddenDim * in2 + hiddenDim +      // слой 2 + смещения
      4 * hiddenDim + 4                  // выход + смещения
    );
  }

  getExpectedHashTableCount() {
    const { levels, featuresPerLevel, hashTableSize } = this._config;
    return levels * hashTableSize * featuresPerLevel;
  }

  /**
   * Загрузка весов, обученных оффлайн.
   *
   * @param {object} data
   *   hashTable — Float32Array признаков решётки
   *   mlpWeights — Float32Array весов MLP (подряд: W1,b1,W2,b2,W3,b3)
   *   config — переопределение параметров решётки
   *   bbox — { min: [x,y,z], max: [x,y,z] } границы сцены
   */
  loadWeights(data) {
    if (!this._backend?.isReady()) return Err('Backend not ready');
    if (!data?.hashTable || !data?.mlpWeights) {
      return Err('Both hashTable and mlpWeights are required');
    }

    if (data.config) this._config = { ...this._config, ...data.config };
    if (data.bbox) this._bbox = { min: [...data.bbox.min], max: [...data.bbox.max] };

    const expectedWeights = this.getExpectedWeightCount();
    if (data.mlpWeights.length < expectedWeights) {
      return Err(
        `MLP weight count mismatch: got ${data.mlpWeights.length}, ` +
        `expected at least ${expectedWeights} for hiddenDim=${this._config.hiddenDim}, ` +
        `levels=${this._config.levels}, featuresPerLevel=${this._config.featuresPerLevel}`
      );
    }

    const expectedHash = this.getExpectedHashTableCount();
    if (data.hashTable.length < expectedHash) {
      return Err(
        `Hash table size mismatch: got ${data.hashTable.length}, expected ${expectedHash}`
      );
    }

    try {
      this._backend.ensureStorageBuffer('nerfHashTable', data.hashTable.byteLength);
      this._backend.writeStorageBuffer('nerfHashTable', data.hashTable);

      this._backend.ensureStorageBuffer('nerfMLPWeights', data.mlpWeights.byteLength);
      this._backend.writeStorageBuffer('nerfMLPWeights', data.mlpWeights);

      this._weightsLoaded = true;
      this._meta = {
        ...data.meta,
        hashEntries: data.hashTable.length,
        mlpWeights: data.mlpWeights.length,
        loadedAt: new Date().toISOString(),
      };

      _log.info(`[NeRF] Веса загружены: ${data.hashTable.length} признаков решётки, ${data.mlpWeights.length} весов MLP`);
      return Ok(true);
    } catch (e) {
      return Err(e.message);
    }
  }

  /**
   * Загрузка весов по URL. Ожидается .bin с заголовком JSON:
   *   [4 байта: длина JSON][JSON][Float32 hashTable][Float32 mlpWeights]
   */
  async loadWeightsFromURL(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) return Err(`HTTP ${response.status} loading ${url}`);

      const buffer = await response.arrayBuffer();
      const view = new DataView(buffer);
      const headerLength = view.getUint32(0, true);

      if (headerLength > buffer.byteLength - 4) {
        return Err('Malformed weights file: header length exceeds file size');
      }

      const headerText = new TextDecoder().decode(new Uint8Array(buffer, 4, headerLength));
      const header = JSON.parse(headerText);

      // Float32Array требует выравнивания смещения на 4 байта
      let offset = 4 + headerLength;
      offset = Math.ceil(offset / 4) * 4;

      const hashCount = header.hashTableCount;
      const hashTable = new Float32Array(buffer, offset, hashCount);
      const mlpWeights = new Float32Array(buffer, offset + hashCount * 4, header.mlpWeightCount);

      return this.loadWeights({
        hashTable, mlpWeights,
        config: header.config,
        bbox: header.bbox,
        meta: { source: url, ...header.meta },
      });
    } catch (e) {
      return Err(`Failed to load NeRF weights: ${e.message}`);
    }
  }

  setBoundingBox(min, max) {
    this._bbox = { min: [...min], max: [...max] };
  }

  /**
   * Рендер нового ракурса. Вызывается внутри открытого кадра бэкенда.
   *
   * @param {object} args
   *   viewProj — Float32Array(16), матрица вида-проекции
   *   cameraPos — [x, y, z]
   *   width, height — разрешение
   *   targetName — имя выходного таргета
   */
  renderNovelView({ viewProj, cameraPos, width, height, targetName = 'nerf' }) {
    if (!this.isReady()) return null;

    const start = performance.now();
    const target = this._backend.ensureTarget(targetName, width, height, FORMATS.HDR, true);
    const invViewProj = mat4.invert(viewProj);

    this._backend.runComputePass('nerfRender', {
      resources: {
        hashTable: this._backend.getStorageBuffer('nerfHashTable'),
        mlpWeights: this._backend.getStorageBuffer('nerfMLPWeights'),
        texDst: target.view,
      },
      uniforms: {
        uInvViewProj: invViewProj,
        uCameraPos: [cameraPos[0], cameraPos[1], cameraPos[2], 1],
        uBBoxMin: [...this._bbox.min, 0],
        uBBoxMax: [...this._bbox.max, 0],
        uResolution: [width, height],
        uSteps: this._config.steps,
        uLevels: this._config.levels,
        uFeaturesPerLevel: this._config.featuresPerLevel,
        uHashTableSize: this._config.hashTableSize,
        uBaseResolution: this._config.baseResolution,
        uPerLevelScale: this._config.perLevelScale,
        uDensityScale: this._config.densityScale,
        uStepJitter: this._config.stepJitter,
        uHiddenDim: this._config.hiddenDim,
        uFrame: this._backend.getFrameIndex(),
      },
      dispatch: [width, height, 1],
    });

    this._lastRenderMs = performance.now() - start;
    return target;
  }

  /**
   * Обучение в браузере не поддерживается — см. комментарий в шапке модуля.
   * Метод оставлен, чтобы вызов не падал молча, а возвращал внятную причину.
   */
  async trainModel() {
    return Err(
      'Обучение NeRF в браузере не поддерживается. WebGPU не предоставляет ' +
      'атомарных операций над f32 и доступа к тензорным ядрам, необходимых ' +
      'для обратного распространения через хеш-решётку. Обучите модель ' +
      'оффлайн (tiny-cuda-nn / nerfstudio / PyTorch) и загрузите веса через ' +
      'loadWeights() или loadWeightsFromURL().'
    );
  }

  dispose() { this._disposed = true; this._weightsLoaded = false; }
}

// ═══════════════════════════════════════════════════════════════════
// VOLUME RENDERER — прямой ray marching медицинского объёма
// ═══════════════════════════════════════════════════════════════════

/** Пресеты окна визуализации (Хаунсфилд), нормализованные в [0,1]. */
export const VOLUME_WINDOW_PRESETS = Object.freeze({
  softTissue: { center: 0.50, width: 0.40, label: 'Мягкие ткани' },
  lung:       { center: 0.25, width: 0.60, label: 'Лёгкие' },
  bone:       { center: 0.70, width: 0.50, label: 'Кость' },
  brain:      { center: 0.52, width: 0.16, label: 'Мозг' },
  liver:      { center: 0.54, width: 0.24, label: 'Печень' },
  angio:      { center: 0.60, width: 0.35, label: 'Ангио' },
  full:       { center: 0.50, width: 1.00, label: 'Полный диапазон' },
});

export class VolumeRenderer {
  constructor(backend, options = {}) {
    this._backend = backend;
    this._volumeTexture = null;
    this._volumeSampler = null;
    this._transferTarget = null;
    this._dims = [0, 0, 0];
    this._spacing = [1, 1, 1];
    this._bbox = { min: [-1, -1, -1], max: [1, 1, 1] };
    this._window = { ...VOLUME_WINDOW_PRESETS.softTissue };
    this._config = {
      steps: options.steps ?? 256,
      shadowSteps: options.shadowSteps ?? 8,
      densityScale: options.densityScale ?? 8.0,
      stepJitter: options.stepJitter ?? 1.0,
      ambient: options.ambient ?? 0.25,
      gradientDelta: options.gradientDelta ?? 0.004,
      lightDir: options.lightDir ?? [0.4, 0.6, 0.7],
    };
    this._disposed = false;
  }

  isReady() { return !!this._volumeTexture && !this._disposed && this._backend?.isReady(); }
  getDimensions() { return [...this._dims]; }
  getWindow() { return { ...this._window }; }

  /**
   * Загрузка воксельного объёма.
   *
   * @param {Uint8Array|Uint16Array|Float32Array} data плотности, порядок x→y→z
   * @param {number[]} dims [width, height, depth]
   * @param {object} options { spacing, normalize }
   */
  loadVolume(data, dims, options = {}) {
    if (!this._backend?.isReady()) return Err('Backend not ready');

    const [w, h, d] = dims;
    const expected = w * h * d;
    if (data.length < expected) {
      return Err(`Volume data too small: got ${data.length}, need ${expected}`);
    }

    // Нормализация в r16float: полный диапазон источника (12-бит КТ,
    // 16-бит МРТ) сжимается в [0,1], окно применяется уже в шейдере
    let normalized;
    if (data instanceof Float32Array && options.normalize === false) {
      normalized = data;
    } else {
      let min = Infinity, max = -Infinity;
      for (let i = 0; i < expected; i++) {
        const v = data[i];
        if (v < min) min = v;
        if (v > max) max = v;
      }
      const range = max - min || 1;
      normalized = new Float32Array(expected);
      for (let i = 0; i < expected; i++) normalized[i] = (data[i] - min) / range;
      _log.debug(`[Volume] Нормализация: [${min}, ${max}] → [0, 1]`);
    }

    try {
      this._volumeTexture?.destroy();

      const device = this._backend.device;
      this._volumeTexture = device.createTexture({
        label: 'volume3d',
        size: { width: w, height: h, depthOrArrayLayers: d },
        dimension: '3d',
        format: 'r32float',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });

      // r32float — 4 байта на воксель; строка выравнивается на 256 байт
      const bytesPerRow = Math.ceil((w * 4) / 256) * 256;
      if (bytesPerRow === w * 4) {
        device.queue.writeTexture(
          { texture: this._volumeTexture },
          normalized,
          { bytesPerRow, rowsPerImage: h },
          { width: w, height: h, depthOrArrayLayers: d }
        );
      } else {
        // Перекладка с паддингом строк
        const padded = new Uint8Array(bytesPerRow * h * d);
        const src = new Uint8Array(normalized.buffer, normalized.byteOffset, expected * 4);
        for (let z = 0; z < d; z++) {
          for (let y = 0; y < h; y++) {
            const srcOffset = (z * h + y) * w * 4;
            const dstOffset = (z * h + y) * bytesPerRow;
            padded.set(src.subarray(srcOffset, srcOffset + w * 4), dstOffset);
          }
        }
        device.queue.writeTexture(
          { texture: this._volumeTexture },
          padded,
          { bytesPerRow, rowsPerImage: h },
          { width: w, height: h, depthOrArrayLayers: d }
        );
      }

      this._volumeView = this._volumeTexture.createView({ dimension: '3d' });
      this._volumeSampler = device.createSampler({
        label: 'volumeSampler',
        magFilter: 'linear', minFilter: 'linear',
        addressModeU: 'clamp-to-edge',
        addressModeV: 'clamp-to-edge',
        addressModeW: 'clamp-to-edge',
      });

      this._dims = [w, h, d];
      this._spacing = options.spacing || [1, 1, 1];

      // Bounding box с учётом физического размера вокселя: без этого
      // анизотропные срезы (типичные для КТ) выглядят сплюснутыми
      const sx = w * this._spacing[0];
      const sy = h * this._spacing[1];
      const sz = d * this._spacing[2];
      const maxDim = Math.max(sx, sy, sz);
      this._bbox = {
        min: [-sx / maxDim, -sy / maxDim, -sz / maxDim],
        max: [sx / maxDim, sy / maxDim, sz / maxDim],
      };

      _log.info(`[Volume] Загружен объём ${w}×${h}×${d}, шаг [${this._spacing.join(', ')}]`);
      return Ok(true);
    } catch (e) {
      _log.error('[Volume] Загрузка не удалась:', e.message);
      return Err(e.message);
    }
  }

  /**
   * Передаточная функция: плотность → RGBA.
   * @param {Array<{position:number, color:[number,number,number], opacity:number}>} stops
   * @param {number} resolution число точек LUT
   */
  setTransferFunction(stops, resolution = 256) {
    if (!this._backend?.isReady()) return Err('Backend not ready');
    if (!Array.isArray(stops) || stops.length < 2) {
      return Err('Transfer function needs at least 2 stops');
    }

    const sorted = [...stops].sort((a, b) => a.position - b.position);
    const lut = new Uint8Array(resolution * 4);

    for (let i = 0; i < resolution; i++) {
      const t = i / (resolution - 1);

      let lo = sorted[0];
      let hi = sorted[sorted.length - 1];
      for (let s = 0; s < sorted.length - 1; s++) {
        if (t >= sorted[s].position && t <= sorted[s + 1].position) {
          lo = sorted[s];
          hi = sorted[s + 1];
          break;
        }
      }

      const span = hi.position - lo.position;
      const f = span > 1e-6 ? (t - lo.position) / span : 0;

      lut[i * 4]     = Math.round(255 * (lo.color[0] + (hi.color[0] - lo.color[0]) * f));
      lut[i * 4 + 1] = Math.round(255 * (lo.color[1] + (hi.color[1] - lo.color[1]) * f));
      lut[i * 4 + 2] = Math.round(255 * (lo.color[2] + (hi.color[2] - lo.color[2]) * f));
      lut[i * 4 + 3] = Math.round(255 * (lo.opacity + (hi.opacity - lo.opacity) * f));
    }

    this._transferTarget = this._backend.uploadData(
      'volumeTransfer', lut, resolution, 1, 'rgba8unorm'
    );
    return Ok(true);
  }

  /** Передаточная функция по умолчанию — мягкие ткани и кость. */
  setDefaultTransferFunction() {
    return this.setTransferFunction([
      { position: 0.00, color: [0.0, 0.0, 0.0], opacity: 0.00 },
      { position: 0.22, color: [0.0, 0.0, 0.0], opacity: 0.00 },
      { position: 0.35, color: [0.72, 0.34, 0.28], opacity: 0.06 },
      { position: 0.50, color: [0.88, 0.62, 0.48], opacity: 0.18 },
      { position: 0.68, color: [0.96, 0.88, 0.76], opacity: 0.42 },
      { position: 0.85, color: [1.00, 0.98, 0.94], opacity: 0.80 },
      { position: 1.00, color: [1.00, 1.00, 1.00], opacity: 1.00 },
    ]);
  }

  setWindow(centerOrPreset, width) {
    if (typeof centerOrPreset === 'string') {
      const preset = VOLUME_WINDOW_PRESETS[centerOrPreset];
      if (!preset) return Err(`Unknown window preset: ${centerOrPreset}`);
      this._window = { center: preset.center, width: preset.width };
      return Ok(true);
    }
    this._window = {
      center: Math.max(0, Math.min(1, centerOrPreset)),
      width: Math.max(0.01, Math.min(2, width)),
    };
    return Ok(true);
  }

  setConfig(partial) {
    this._config = { ...this._config, ...partial };
    return Ok(true);
  }

  getBoundingBox() { return { min: [...this._bbox.min], max: [...this._bbox.max] }; }

  /** Рендер объёма. Вызывается внутри открытого кадра бэкенда. */
  render({ viewProj, cameraPos, width, height, targetName = 'volume' }) {
    if (!this.isReady()) return null;
    if (!this._transferTarget) this.setDefaultTransferFunction();

    const target = this._backend.ensureTarget(targetName, width, height, FORMATS.HDR, true);
    const invViewProj = mat4.invert(viewProj);
    const ld = this._config.lightDir;

    this._backend.runComputePass('volumeRaymarch', {
      resources: {
        texVolume: this._volumeView,
        sampVolume: this._volumeSampler,
        texTransfer: this._transferTarget.view,
        texDst: target.view,
      },
      uniforms: {
        uInvViewProj: invViewProj,
        uCameraPos: [cameraPos[0], cameraPos[1], cameraPos[2], 1],
        uBBoxMin: [...this._bbox.min, 0],
        uBBoxMax: [...this._bbox.max, 0],
        uLightDir: [ld[0], ld[1], ld[2], 0],
        uResolution: [width, height],
        uSteps: this._config.steps,
        uShadowSteps: this._config.shadowSteps,
        uDensityScale: this._config.densityScale,
        uWindowCenter: this._window.center,
        uWindowWidth: this._window.width,
        uStepJitter: this._config.stepJitter,
        uAmbient: this._config.ambient,
        uGradientDelta: this._config.gradientDelta,
        uFrame: this._backend.getFrameIndex(),
        uPad0: 0,
      },
      dispatch: [width, height, 1],
    });

    return target;
  }

  dispose() {
    this._disposed = true;
    try { this._volumeTexture?.destroy(); } catch { /* уже уничтожена */ }
    this._volumeTexture = null;
    this._volumeView = null;
    this._volumeSampler = null;
  }
}
